<script setup lang="ts">
interface Props {
  image: string
  planName: string
  price: number
  features: string[]
  bannerText?: string
  color?: string
  backgroundImage?: string
}

const props = withDefaults(defineProps<Props>(), {
  color: '#B91C1C',
  backgroundImage: 'https://picsum.photos/400/500?random=1',
  features: () => []
})

const cardStyle = computed(() => ({
  '--card-accent-color': props.color,
  '--card-bg-overlay': `${props.color}33`
}))
</script>

<template>
  <div
    class="relative flex flex-col gap-6 p-6 overflow-hidden border border-white/10 rounded-3xl shadow-xl bg-neutral-900 group"
    :style="cardStyle"
  >
    <!-- Background Image with Accent Overlay -->
    <div class="absolute inset-0 z-0">
      <img
        :src="backgroundImage"
        class="object-cover w-full h-full opacity-40 mix-blend-luminosity"
        alt=""
      >
      <div
        class="absolute inset-0"
        :style="{ backgroundColor: 'var(--card-bg-overlay)' }"
      />
    </div>

    <!-- Banner -->
    <div
      v-if="bannerText"
      class="absolute top-0 right-0 z-20 overflow-hidden w-40 h-40"
    >
      <div
        class="absolute top-7 -right-12 bg-white/20 backdrop-blur-md border border-white/20 px-12 py-1 rotate-45 text-[13px] font-bold text-white uppercase shadow-lg text-center min-w-[200px]"
        :style="{ backgroundColor: `${props.color}CC` }"
      >
        {{ bannerText }}
      </div>
    </div>

    <!-- Header: Image, Plan Name, Price -->
    <div class="relative z-10 flex items-center gap-4">
      <div class="w-20 h-20 overflow-hidden rounded-2xl bg-black/40 backdrop-blur-md border border-white/10">
        <img
          :src="image"
          :alt="planName"
          class="object-cover w-full h-full p-2"
        >
      </div>
      <div class="flex flex-col">
        <h3 class="text-xl font-bold text-white font-base">
          {{ planName }} Plan
        </h3>
        <div class="flex items-baseline gap-1 text-white font-base">
          <span class="text-3xl font-bold">${{ Math.floor(price) }}.</span>
          <span class="text-2xl font-bold">{{ (price % 1).toFixed(2).split('.')[1] || '00' }}</span>
          <span class="text-xl font-bold ml-1">/ M</span>
        </div>
      </div>
    </div>

    <!-- Body: Features -->
    <div class="relative z-10 flex-1 p-5 rounded-2xl bg-black/40 backdrop-blur-lg border border-white/5 shadow-inner">
      <ul class="flex flex-col gap-4">
        <li
          v-for="feature in features"
          :key="feature"
          class="flex items-center gap-3 text-white/90 font-medium font-base"
        >
          <div class="flex items-center justify-center w-5 h-5 rounded-full border border-white/30 group-hover:border-white/50 transition-colors bg-white/5 shadow-[0_0_10px_rgba(255,255,255,0.05)]">
            <UIcon
              name="i-heroicons-check-20-solid"
              class="w-3 h-3"
            />
          </div>
          {{ feature }}
        </li>
      </ul>
    </div>

    <!-- Footer: Buy Plan Button -->
    <button
      class="relative z-10 flex items-center justify-center gap-2 w-full py-4 px-6 rounded-2xl text-white font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
      :style="{ backgroundColor: props.color }"
    >
      <span>Buy Plan</span>
      <UIcon
        name="i-heroicons-chevron-right-20-solid"
        class="w-5 h-5"
      />
      <!-- Shine effect -->
      <div class="absolute inset-0 overflow-hidden rounded-2xl">
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shine_1.5s_infinite]" />
      </div>
    </button>
  </div>
</template>

<style scoped>
@keyframes shine {
  100% {
    transform: translateX(100%);
  }
}

/* Any additional custom styles can go here */
</style>
